import type { ReactElement } from 'react';
import { Box, Chip, Tooltip } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Autorenew,
  Block,
  Cancel,
  CheckCircle,
  DialerSip,
  DoNotDisturb,
  DoNotDisturbOn,
  Error as ErrorIcon,
  HighlightOff,
  ManageSearch,
  PhoneInTalk,
  PhoneMissed,
  RingVolume,
  Schedule,
  SignalCellularOff,
  TimerOff,
  Verified,
  VoiceOverOff,
  Voicemail,
  Warning,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import type { CallStatus } from '../types';

type StatusColor = 'default' | 'success' | 'error' | 'warning' | 'info';

interface CallStatusChipProps {
  status?: CallStatus;
  isConverted?: boolean;
  isOptout?: boolean;
  isMismatch?: boolean;
  size?: 'small' | 'medium';
  sx?: SxProps<Theme>;
}

const statusConfig: Record<CallStatus, { labelKey: string; color: StatusColor; icon: ReactElement }> = {
  pending: { labelKey: 'components:statusChips.call.labels.pending', color: 'default', icon: <Schedule sx={{ fontSize: 16 }} /> },
  queued: { labelKey: 'components:statusChips.call.labels.queued', color: 'default', icon: <Schedule sx={{ fontSize: 16 }} /> },
  waiting_capacity: { labelKey: 'components:statusChips.call.labels.waiting_capacity', color: 'warning', icon: <Schedule sx={{ fontSize: 16 }} /> },
  waiting_operating_hours: { labelKey: 'components:statusChips.call.labels.waiting_operating_hours', color: 'warning', icon: <Schedule sx={{ fontSize: 16 }} /> },
  dialing: { labelKey: 'components:statusChips.call.labels.dialing', color: 'info', icon: <DialerSip sx={{ fontSize: 16 }} /> },
  analyzing: { labelKey: 'components:statusChips.call.labels.analyzing', color: 'info', icon: <ManageSearch sx={{ fontSize: 16 }} /> },
  ringing: { labelKey: 'components:statusChips.call.labels.ringing', color: 'info', icon: <RingVolume sx={{ fontSize: 16 }} /> },
  in_progress: { labelKey: 'components:statusChips.call.labels.in_progress', color: 'info', icon: <PhoneInTalk sx={{ fontSize: 16 }} /> },
  finalizing: { labelKey: 'components:statusChips.call.labels.finalizing', color: 'warning', icon: <Autorenew sx={{ fontSize: 16 }} /> },
  completed: { labelKey: 'components:statusChips.call.labels.completed', color: 'success', icon: <CheckCircle sx={{ fontSize: 16 }} /> },
  no_interaction: { labelKey: 'components:statusChips.call.labels.no_interaction', color: 'warning', icon: <VoiceOverOff sx={{ fontSize: 16 }} /> },
  failed: { labelKey: 'components:statusChips.call.labels.failed', color: 'error', icon: <ErrorIcon sx={{ fontSize: 16 }} /> },
  no_answer: { labelKey: 'components:statusChips.call.labels.no_answer', color: 'warning', icon: <PhoneMissed sx={{ fontSize: 16 }} /> },
  no_answer_timeout: { labelKey: 'components:statusChips.call.labels.no_answer_timeout', color: 'warning', icon: <PhoneMissed sx={{ fontSize: 16 }} /> },
  busy: { labelKey: 'components:statusChips.call.labels.busy', color: 'warning', icon: <DoNotDisturbOn sx={{ fontSize: 16 }} /> },
  cancelled: { labelKey: 'components:statusChips.call.labels.cancelled', color: 'default', icon: <Cancel sx={{ fontSize: 16 }} /> },
  transfer_unanswered: { labelKey: 'components:statusChips.call.labels.transfer_unanswered', color: 'warning', icon: <PhoneMissed sx={{ fontSize: 16 }} /> },
  congestion: { labelKey: 'components:statusChips.call.labels.congestion', color: 'warning', icon: <Warning sx={{ fontSize: 16 }} /> },
  voicemail: { labelKey: 'components:statusChips.call.labels.voicemail', color: 'warning', icon: <Voicemail sx={{ fontSize: 16 }} /> },
  invalid_number: { labelKey: 'components:statusChips.call.labels.invalid_number', color: 'error', icon: <Warning sx={{ fontSize: 16 }} /> },
  config_error: { labelKey: 'components:statusChips.call.labels.config_error', color: 'error', icon: <Warning sx={{ fontSize: 16 }} /> },
  declined: { labelKey: 'components:statusChips.call.labels.declined', color: 'warning', icon: <Block sx={{ fontSize: 16 }} /> },
  unreachable: { labelKey: 'components:statusChips.call.labels.unreachable', color: 'warning', icon: <SignalCellularOff sx={{ fontSize: 16 }} /> },
  timeout: { labelKey: 'components:statusChips.call.labels.timeout', color: 'warning', icon: <TimerOff sx={{ fontSize: 16 }} /> },
  mismatch: { labelKey: 'components:statusChips.call.labels.mismatch', color: 'warning', icon: <Warning sx={{ fontSize: 16 }} /> },
  api_timeout: { labelKey: 'components:statusChips.call.labels.api_timeout', color: 'error', icon: <TimerOff sx={{ fontSize: 16 }} /> },
  ai_provider_path_failed: { labelKey: 'components:statusChips.call.labels.ai_provider_path_failed', color: 'warning', icon: <SignalCellularOff sx={{ fontSize: 16 }} /> },
  terminated_manual: { labelKey: 'components:statusChips.call.labels.terminated_manual', color: 'error', icon: <HighlightOff sx={{ fontSize: 16 }} /> },
  capacity_timeout: { labelKey: 'components:statusChips.call.labels.capacity_timeout', color: 'error', icon: <TimerOff sx={{ fontSize: 16 }} /> },
  operating_window_timeout: { labelKey: 'components:statusChips.call.labels.operating_window_timeout', color: 'error', icon: <TimerOff sx={{ fontSize: 16 }} /> },
};

function getPaletteColor(theme: Theme, color: StatusColor) {
  if (color === 'default') {
    return {
      bg: theme.palette.action.selected,
      text: theme.palette.text.secondary,
    };
  }
  return {
    bg: theme.palette[color].light,
    text: theme.palette[color].contrastText,
  };
}

export default function CallStatusChip({
  status,
  isConverted = false,
  isOptout = false,
  isMismatch = false,
  size = 'small',
  sx,
}: CallStatusChipProps) {
  const theme = useTheme();
  const { t } = useTranslation(['components']);
  const showOutcome = isConverted || isOptout || isMismatch;

  if (showOutcome) {
    const outcomeKey = isConverted ? 'converted' : isOptout ? 'optout' : 'mismatch';
    const outcomeIcon = isConverted
      ? <Verified sx={{ fontSize: 16 }} />
      : isOptout
        ? <DoNotDisturb sx={{ fontSize: 16 }} />
        : <Warning sx={{ fontSize: 16 }} />;
    const outcomeLabel = t(`components:statusChips.callOutcome.labels.${outcomeKey}`);
    const outcomeDescription = t(`components:statusChips.callOutcome.descriptions.${outcomeKey}`);
    const outcomeColor: StatusColor = isConverted ? 'success' : isOptout ? 'warning' : 'error';
    const palette = getPaletteColor(theme, outcomeColor);

    return (
      <Tooltip title={outcomeDescription} arrow>
        <Box component="span">
          <Chip
            icon={outcomeIcon}
            label={outcomeLabel}
            size={size}
            sx={{
              bgcolor: palette.bg,
              color: palette.text,
              fontWeight: 600,
              textTransform: 'none',
              px: 0.5,
              py: 1,
              '& .MuiChip-icon': { fill: palette.text },
              ...sx,
            }}
          />
        </Box>
      </Tooltip>
    );
  }

  if (!status) return null;
  const config = statusConfig[status];
  const description = t(`components:statusChips.call.descriptions.${status}`);
  const palette = getPaletteColor(theme, config.color);

  return (
    <Tooltip title={description} arrow>
      <Box component="span">
        <Chip
          icon={config.icon}
          label={t(config.labelKey, status)}
          size={size}
          sx={{
            bgcolor: palette.bg,
            color: palette.text,
            fontWeight: 600,
            px: 0.5,
            py: 1,
            textTransform: 'none',
            '& .MuiChip-icon': { fill: palette.text },
            ...sx,
          }}
        />
      </Box>
    </Tooltip>
  );
}
